import { useEffect, useMemo } from 'react';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';

interface ActivityRole {
  activityRoleId: string;
  icon: string;
  name: string;
  tasksCompleted: boolean;
}

export interface TaskBadge {
  taskId: string;
  title: string;
  icon: string;
  status: 'completed' | 'pending' | 'incomplete';
}

interface VolunteerInfo {
  volunteerId: string;
  activityRoles: ActivityRole[];
  taskBadges: TaskBadge[];
}

interface BatchVolunteerInfoResponse {
  volunteers: VolunteerInfo[];
}

export function useBatchRosterVolunteerInfo(activityId: string, volunteerIds: string[]) {
  const apiUrl = useApiUrl();

  const { run, response, isLoading, error } = useCampfireFetch<BatchVolunteerInfoResponse>({
    defer: true,
  });

  const volunteerIdsKey = volunteerIds.join(',');

  useEffect(() => {
    if (volunteerIds.length === 0 || !activityId) {
      return;
    }

    run({
      url: `${apiUrl}/vm/volunteer/roster/batch-info`,
      method: 'POST',
      data: {
        activityId,
        volunteerIds,
      },
    });
  }, [volunteerIdsKey, activityId, apiUrl]);

  const data = useMemo(() => {
    if (!response?.data?.data?.volunteers) {
      return {};
    }

    const lookup: Record<string, { activityRoles: ActivityRole[]; taskBadges: TaskBadge[] }> = {};
    response.data.data.volunteers.forEach((volunteer) => {
      lookup[volunteer.volunteerId] = {
        activityRoles: volunteer.activityRoles,
        taskBadges: volunteer.taskBadges ?? [],
      };
    });

    return lookup;
  }, [response]);

  return { data, isLoading, error };
}
